import { useCallback, useState } from "react";

import yup from "utilities/form/yup";

import { autofillUrlSchema } from "commonComponents/utilities/SelectionOption/SelectionOption.utils";

export interface IUseAutofillUrlFieldResult {
    url: string;
    urlError: string | null;
    validateUrl: (value: string) => Promise<boolean>;
    handleUrlChange: (value: string) => void;
    handleUrlBlur: () => void;
    reset: (nextUrl?: string) => void;
}

/**
 * Shared URL input state + validation for the "Autofill from URL" experience. Every returned
 * function is memoized with an empty/stable dependency list — they only ever touch useState
 * setters (already stable) or module-level values — so consumers can safely put them in their
 * own effect dependency arrays without triggering re-runs on every render.
 */
export function useAutofillUrlField(initialUrl: string = ""): IUseAutofillUrlFieldResult {
    const [url, setUrl] = useState(initialUrl);
    const [urlError, setUrlError] = useState<string | null>(null);

    const validateUrl = useCallback(async (value: string): Promise<boolean> => {
        try {
            await autofillUrlSchema.validate(value);
            setUrlError(null);
            return true;
        } catch (err: unknown) {
            if (err instanceof yup.ValidationError) {
                setUrlError(err.message);
            }
            return false;
        }
    }, []);

    const handleUrlChange = useCallback((value: string) => {
        setUrl(value);
        setUrlError(null);
    }, []);

    const handleUrlBlur = useCallback(() => {
        if (url.trim()) {
            void validateUrl(url);
        }
    }, [url, validateUrl]);

    const reset = useCallback((nextUrl: string = "") => {
        setUrl(nextUrl);
        setUrlError(null);
    }, []);

    return { url, urlError, validateUrl, handleUrlChange, handleUrlBlur, reset };
}
